import React from 'react';
import { Link } from 'react-router-dom';
import ViewAllHeader from '../common/ViewAllHeader';
import HorizontalNewsCard from './HorizontalNewsCard';

const ComplexEntertainmentSection = ({ news = [], videos = [] }) => {
  // Ensure we have enough items, fallback to repeating if needed
  const leftFeatured = news[0] || {};
  // Pad the array so the 2x2 grid always has 4 items
  const paddedGridNews = [...news, ...news, ...news];
  const leftGridItems = paddedGridNews.slice(1, 5);
  const listItems = news.slice(5, 13);
  // Ensure we always have exactly 6 videos to fill the grid completely
  const paddedVideos = [...videos, ...videos, ...videos, ...videos];
  const videoItems = paddedVideos.slice(0, 6);

  return (
    <div className="w-full mt-10 md:mt-14 mb-8">
      {/* SECTION HEADER using standard ViewAllHeader */}
      <ViewAllHeader title="मनोरंजन" to="/category/entertainment" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-8 mt-6">
        
        {/* ================= LEFT COLUMN: Visual Featured & Grid ================= */}
        <div className="lg:col-span-4 flex flex-col gap-6">
          {/* Featured Large Card */}
          {leftFeatured && (
            <Link to={`/news/${leftFeatured.id}`} className="group relative block w-full aspect-[4/3] rounded-lg overflow-hidden shadow-sm">
              <img src={leftFeatured.image} alt={leftFeatured.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white text-lg md:text-xl font-bold leading-tight group-hover:text-red-400 transition drop-shadow-md">
                  {leftFeatured.title}
                </h3>
              </div>
            </Link>
          )}

          {/* 2x2 Grid of Small Cards */}
          <div className="grid grid-cols-2 gap-4">
            {leftGridItems.map((item, idx) => (
              <Link key={idx} to={`/news/${item.id}`} className="group flex flex-col gap-2">
                <div className="w-full aspect-video rounded-md overflow-hidden bg-gray-100">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                </div>
                <h4 className="text-gray-900 font-bold text-[13px] md:text-sm leading-snug line-clamp-3 group-hover:text-brand-red transition">
                  {item.title}
                </h4>
              </Link>
            ))}
          </div>
        </div>

        {/* ================= MIDDLE COLUMN: Bullet List ================= */}
        <div className="lg:col-span-4 border-l-0 md:border-l lg:border-l border-gray-200 md:pl-6 lg:pl-8">
          <ul className="flex flex-col">
            {listItems.length > 0 ? listItems.map((item, idx) => (
              <li key={idx} className="border-b border-gray-200 last:border-b-0 py-3.5 first:pt-0">
                <Link to={`/news/${item.id}`} className="flex gap-3 group">
                  <span className="text-gray-300 group-hover:text-brand-red transition text-lg leading-none mt-0.5">•</span>
                  <span className="text-gray-800 font-semibold text-[15px] leading-snug group-hover:text-brand-red transition">
                    {item.title}
                  </span>
                </Link>
              </li>
            )) : (
              // Fallback if not enough data
              [1,2,3,4,5,6,7,8].map((i) => (
                <li key={i} className="border-b border-gray-200 last:border-b-0 py-3.5 first:pt-0">
                  <a href="#" className="flex gap-3 group">
                    <span className="text-gray-300 group-hover:text-brand-red transition text-lg leading-none mt-0.5">•</span>
                    <span className="text-gray-800 font-semibold text-[15px] leading-snug group-hover:text-brand-red transition">
                      यह मनोरंजन अनुभाग सूची आइटम के लिए एक प्लेसहोल्डर शीर्षक है।
                    </span>
                  </a>
                </li>
              ))
            )}
          </ul>
        </div>

        {/* ================= RIGHT COLUMN: Bollywood Videos ================= */}
        <div className="lg:col-span-4 border-l-0 lg:border-l border-gray-200 lg:pl-8 mt-8 lg:mt-0">
          <div className="flex justify-between items-end mb-4 border-b border-gray-200 pb-2">
            <Link to="/gallery/videos" className="text-[20px] font-black text-gray-900 hover:text-brand-red transition flex items-center gap-1">
             लेटेस्ट वीडियो <i className="fa-solid fa-angle-right text-sm mt-0.5 ml-1 text-gray-400"></i>
            </Link>
            <Link to="/gallery/videos" className="text-xs font-bold text-gray-500 hover:text-brand-red uppercase tracking-wider mb-0.5">
              सभी देखें
            </Link>
          </div>
          
          <div className="grid grid-cols-2 gap-x-4 gap-y-6">
            {videoItems.map((video, idx) => (
              <Link key={idx} to={`/news/${video.id}`} className="group flex flex-col gap-2">
                <div className="relative w-full aspect-video rounded-md overflow-hidden bg-black shadow-sm">
                  <img src={video.image} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500 opacity-90 group-hover:opacity-100" />
                  
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                     <div className="w-8 h-8 rounded-full bg-brand-red/90 flex items-center justify-center shadow-lg">
                       <i className="fa-solid fa-play text-white text-[10px] ml-0.5"></i>
                     </div>
                  </div>
                  
                  {/* Duration Badge */}
                  <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow">
                    {video.readTime || "04:30"}
                  </div>
                </div>
                <h4 className="text-gray-900 font-bold text-[13px] leading-snug line-clamp-3 group-hover:text-brand-red transition">
                  {video.title}
                </h4>
              </Link>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ComplexEntertainmentSection;
